import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";

export default function Header({ lang, onSwitchLang }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const base = `/${lang}`;
  const isActive = (path) => location.pathname === path;

  const links = [
    { href: `${base}#sectors`, label: t("header.nav.sectors"), key: "sectors" },
    { href: `${base}#how`, label: t("header.nav.how_it_works"), key: "how" },
    { href: `${base}#transparency`, label: t("header.nav.transparency"), key: "transparency" },
    { href: `${base}#newsletter`, label: t("header.nav.newsletter"), key: "newsletter" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-[#0F2447] text-white border-b border-[#0a1a35]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to={base} className="flex items-center gap-3 group" data-testid="header-logo">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-[#B85D3D] text-white font-serif text-lg font-semibold">X</span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-xl tracking-wide">{t("header.brand")}</span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#c9d3e4]">{t("header.brand_subtitle")}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          <Link
            to={base}
            className={`text-sm tracking-wide hover:text-[#E9B384] ${isActive(base) ? "text-[#E9B384]" : "text-white"}`}
            data-testid="nav-home"
          >
            {t("header.nav.home")}
          </Link>
          {links.map((l) => (
            <a key={l.key} href={l.href} className="text-sm tracking-wide text-white hover:text-[#E9B384]" data-testid={`nav-${l.key}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1 border border-[#26436e] rounded-md overflow-hidden text-xs" aria-label={t("header.language.label")}>
            <button
              type="button"
              onClick={() => onSwitchLang("ar")}
              className={`px-3 py-1.5 uppercase tracking-widest ${lang === "ar" ? "bg-[#B85D3D] text-white" : "bg-transparent text-[#c9d3e4] hover:text-white"}`}
              data-testid="lang-switch-ar"
              aria-pressed={lang === "ar"}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => onSwitchLang("en")}
              className={`px-3 py-1.5 uppercase tracking-widest ${lang === "en" ? "bg-[#B85D3D] text-white" : "bg-transparent text-[#c9d3e4] hover:text-white"}`}
              data-testid="lang-switch-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#26436e]"
            aria-label={open ? t("header.close_menu") : t("header.open_menu")}
            aria-expanded={open}
            data-testid="hamburger-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0F2447] border-t border-[#0a1a35]" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-3">
            <Link to={base} onClick={() => setOpen(false)} className="py-2 text-sm text-white" data-testid="m-nav-home">{t("header.nav.home")}</Link>
            {links.map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="py-2 text-sm text-white" data-testid={`m-nav-${l.key}`}>{l.label}</a>
            ))}
            <div className="pt-3 mt-1 border-t border-[#26436e] flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-[#c9d3e4] mr-2">{t("header.language.label")}:</span>
              <button
                type="button"
                onClick={() => { onSwitchLang("ar"); setOpen(false); }}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded ${lang === "ar" ? "bg-[#B85D3D] text-white" : "border border-[#26436e] text-white"}`}
                data-testid="m-lang-ar"
              >
                ES
              </button>
              <button
                type="button"
                onClick={() => { onSwitchLang("en"); setOpen(false); }}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded ${lang === "en" ? "bg-[#B85D3D] text-white" : "border border-[#26436e] text-white"}`}
                data-testid="m-lang-en"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
